
import { supabase } from "@/integrations/supabase/client";
import { locationService } from "@/services/locationService";
import { UserProfile } from "@/types/gprofile";

export class GprofileService {
  static async fetchProfileData(userId: string): Promise<UserProfile | null> {
    console.log('🔍 Attempting to fetch profile with RLS...');
    
    // Fetch the user profile with RLS respect - only visible profiles, including precision fields
    const { data: profileData, error: profileError } = await supabase
      .from('user_profiles')
      .select('*, location_precision_meters, show_location_precision')
      .eq('user_id', userId)
      .eq('is_visible', true)
      .maybeSingle();

    console.log('📊 Profile query result:', { profileData, profileError });

    if (profileError) {
      console.error('❌ Profile fetch error:', profileError);
      
      // Try fetching without the is_visible filter for debugging
      console.log('🔍 Attempting to fetch profile without visibility filter...');
      const { data: debugProfileData, error: debugError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      
      console.log('📊 Debug profile query result:', { debugProfileData, debugError });
      
      if (debugProfileData && !debugProfileData.is_visible) {
        console.log('❌ Profile exists but is not visible');
        throw new Error('This profile is not visible');
      }
      
      throw profileError;
    }

    if (!profileData) {
      console.log('❌ No visible profile found for userId:', userId);
      
      // Additional debugging - check if profile exists at all
      const { data: anyProfileData } = await supabase
        .from('user_profiles')
        .select('user_id, is_visible')
        .eq('user_id', userId)
        .maybeSingle();
      
      console.log('📊 Any profile check:', anyProfileData);
      
      throw new Error('Profile not found or not visible');
    }

    console.log('✅ Profile data found:', profileData);
    return profileData;
  }

  static async fetchUserStatus(userId: string) {
    console.log('🔍 Fetching user status...');
    
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('last_seen, is_verified')
      .eq('id', userId)
      .maybeSingle();

    console.log('📊 User status query result:', { userData, userError });

    if (userError) {
      console.warn('⚠️ User status fetch warning:', userError);
    }

    return userData;
  }

  static async checkUserBlocked(userId: string, currentUserId: string): Promise<boolean> {
    const { data: blockData } = await supabase
      .from('user_blocks')
      .select('id')
      .eq('blocker_id', userId)
      .eq('blocked_id', currentUserId)
      .maybeSingle();

    return !!blockData;
  }

  static calculateOnlineStatus(lastSeen: string | null): boolean {
    if (!lastSeen) return false;
    
    const lastSeenDate = new Date(lastSeen);
    const now = new Date();
    return (now.getTime() - lastSeenDate.getTime()) < 5 * 60 * 1000;
  }

  static async calculateDistance(currentUserId: string, targetUserId: string): Promise<number | null> {
    try {
      const distance = await locationService.calculateUserDistance(currentUserId, targetUserId);
      console.log('📍 Distance calculated:', distance);
      return distance;
    } catch (error) {
      console.warn('⚠️ Distance calculation failed:', error);
      return null;
    }
  }
}
